import { useState, useEffect } from 'react';
import type { NudgeConfig } from '../../hooks/useConversionTriggers';
import { UpgradeModal } from './UpgradeModal';
import { useSystemSettings } from '../../hooks/useSystemSettings';

interface Props {
  nudge: NudgeConfig | null;
  onDismiss: () => void;
  onUpgradeClick: () => void;
}

const URGENCY_STYLES: Record<string, { bg: string; border: string; icon: string; btn: string }> = {
  high: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    btn: 'bg-red-500 hover:bg-red-600',
  },
  medium: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'text-emerald-600',
    btn: 'bg-emerald-500 hover:bg-emerald-600',
  },
  low: {
    bg: 'bg-white',
    border: 'border-gray-200',
    icon: 'text-amber-500',
    btn: 'bg-emerald-500 hover:bg-emerald-600',
  },
};

export const ConversionNudge = ({ nudge, onDismiss, onUpgradeClick }: Props) => {
  const [visible, setVisible] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { settings } = useSystemSettings();
  const appPrimary = settings.app_primary_color || '#10b981';

  // Animate in when nudge appears
  useEffect(() => {
    if (nudge) {
      const t = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(t);
    } else {
      setVisible(false);
    }
  }, [nudge]);

  if (!nudge) return null;

  const styles = URGENCY_STYLES[nudge.urgency] ?? URGENCY_STYLES.medium;

  const handleCta = () => {
    onUpgradeClick();
    setShowUpgrade(true);
  };

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  };

  return (
    <>
      {/* Slide-in nudge banner */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(480px,calc(100vw-2rem))] transition-all duration-300 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
        }`}
      >
        <div
          className={`${styles.bg} ${styles.border} border rounded-2xl px-4 py-3.5 flex items-center gap-3`}
          style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
        >
          {/* Icon */}
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${appPrimary}18` }}
          >
            <i className={`${nudge.icon} text-base ${styles.icon}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-700 text-gray-900 leading-tight truncate">{nudge.title}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">{nudge.message}</p>
          </div>

          {/* CTA */}
          <button
            onClick={handleCta}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-2 ${styles.btn} text-white text-xs font-700 rounded-xl cursor-pointer whitespace-nowrap transition-colors`}
          >
            <i className="ri-arrow-right-up-line text-xs" />
            {nudge.cta}
          </button>

          {/* Dismiss */}
          <button
            onClick={handleDismiss}
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-xl hover:bg-black/5 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            title="Fechar"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      </div>

      {/* Upgrade modal triggered from nudge */}
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        trigger={nudge.type}
        resource={nudge.resource}
        feature={nudge.feature}
      />
    </>
  );
};
